import React, { useState, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  Image,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";
import { router, useFocusEffect } from "expo-router";
import { useDispatch, useSelector } from "react-redux";
import i18n from "../../locales/i18n";
import customAxios from "../../utils/customAxios";
import WebSidebar from "./WebSidebar";
import { RootState } from "../../store";
import { appActions } from "../../store/slices/appSlice";

// Import settings image for header
import SettingsImage from "../../assets/images/settings.png";

const WebPasswordScreen: React.FC = () => {
  const dispatch = useDispatch();
  const { locale } = useSelector((state: RootState) => state.app);

  // Check if current language is RTL (Arabic)
  const isRTL = locale === "ar" || i18n.locale === "ar";

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Password strength calculation
  const getPasswordStrength = (password: string) => {
    if (!password) return { score: 0, label: "", color: "#E5E7EB" };

    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;

    if (score <= 2)
      return { score: 33, label: i18n.t("weak"), color: "#EF4444" };
    if (score <= 3)
      return {
        score: 66,
        label: i18n.t("medium"),
        color: "#F59E0B",
      };
    return { score: 100, label: i18n.t("strong"), color: "#10B981" };
  };

  const passwordStrength = getPasswordStrength(newPassword);

  // Update locale when screen is focused
  useFocusEffect(
    useCallback(() => {
      dispatch(appActions.setLocale(i18n.locale));
    }, [dispatch])
  );

  const handleGoBack = () => {
    router.push("/(drawer)/params/profile" as any);
  };

  const handleSave = async () => {
    setMessage(null);

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setMessage({
        type: "error",
        text: i18n.t("fillAllFields"),
      });
      return;
    }

    if (newPassword.length < 5) {
      setMessage({
        type: "error",
        text: i18n.t("passwordMinLength"),
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage({
        type: "error",
        text: i18n.t("passwordsNotMatch"),
      });
      return;
    }

    if (currentPassword === newPassword) {
      setMessage({
        type: "error",
        text:
          i18n.t("passwordCannotBeSame"),
      });
      return;
    }

    setLoading(true);
    try {
      const response = await customAxios.patch(
        `/users/updatepassword`,
        {
          password: currentPassword,
          newpassword: newPassword,
        },
        { withCredentials: true }
      );

      setMessage({
        type: "success",
        text:
          response.data?.message ||
          i18n.t("passwordChangeSuccess") ||
          "تم تغيير كلمة المرور بنجاح",
      });

      // Clear form fields
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");

      // Navigate back after success
      setTimeout(() => {
        router.back();
      }, 1500);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        i18n.t("changePasswordError") ||
        "حدث خطأ أثناء تغيير كلمة المرور";
      setMessage({ type: "error", text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      key={`password-screen-${locale}`}
      className="flex flex-1 bg-[#F5F5F5] min-h-screen flex-row"
    >
      <WebSidebar />

      <View style={styles.mainContent}>
        <ScrollView
          style={styles.scrollView}
          showsVerticalScrollIndicator={false}
        >
          {/* Header with breadcrumb and blue bar */}
          <View style={styles.headerSection}>
            <View style={styles.header}>
              <View style={styles.breadcrumb}>
                <TouchableOpacity onPress={handleGoBack}>
                  <Text style={styles.breadcrumbLink}>
                    {i18n.t("comptepersonel")}
                  </Text>
                </TouchableOpacity>
                <Text style={styles.breadcrumbSeparator}>{"  >  "}</Text>
                <Text style={styles.breadcrumbCurrent}>
                  {i18n.t("changePassword")}
                </Text>
              </View>
              <View style={styles.headerIconContainer}>
                <Image
                  source={SettingsImage}
                  style={styles.headerIcon}
                  resizeMode="contain"
                />
              </View>
            </View>
            <View style={styles.headerBar} />
          </View>

          {/* Message Display */}
          {message && (
            <View
              style={[
                styles.messageContainer,
                message.type === "error"
                  ? styles.errorMessage
                  : styles.successMessage,
              ]}
            >
              <Text style={styles.messageText}>{message.text}</Text>
            </View>
          )}

          {/* Info Box */}
          <View style={styles.infoBox}>
            <Text style={styles.infoIcon}>ℹ️</Text>
            <Text style={styles.infoText}>
              {i18n.t("changePasswordInfo") ||
                "لأمانك، اختر كلمة مرور قوية تحتوي على 8 أحرف على الأقل"}
            </Text>
          </View>

          {/* Password Form */}
          <View style={styles.formContainer}>
            {/* Current Password */}
            <View style={styles.fieldContainer}>
              <Text style={styles.fieldLabel}>
                {i18n.t("currentPasswordLabel")}
              </Text>
              <View style={styles.passwordField}>
                <TouchableOpacity
                  onPress={() => setShowCurrentPassword(!showCurrentPassword)}
                  style={styles.eyeButton}
                >
                  <Text style={styles.eyeIcon}>
                    {showCurrentPassword ? "👁️" : "🙈"}
                  </Text>
                </TouchableOpacity>
                <TextInput
                  style={styles.passwordInput}
                  value={currentPassword}
                  onChangeText={setCurrentPassword}
                  secureTextEntry={!showCurrentPassword}
                  placeholder="••••••••••••••••"
                  placeholderTextColor="#9CA3AF"
                />
              </View>
            </View>

            {/* New Password */}
            <View style={styles.fieldContainer}>
              <Text style={styles.fieldLabel}>
                {i18n.t("newPassword")}
              </Text>
              <View style={styles.passwordField}>
                <TouchableOpacity
                  onPress={() => setShowNewPassword(!showNewPassword)}
                  style={styles.eyeButton}
                >
                  <Text style={styles.eyeIcon}>
                    {showNewPassword ? "👁️" : "🙈"}
                  </Text>
                </TouchableOpacity>
                <TextInput
                  style={styles.passwordInput}
                  value={newPassword}
                  onChangeText={setNewPassword}
                  secureTextEntry={!showNewPassword}
                  placeholder="••••••••••••••••"
                  placeholderTextColor="#9CA3AF"
                />
              </View>

              {/* Password Strength */}
              {newPassword.length > 0 && (
                <View style={styles.strengthContainer}>
                  <View style={styles.strengthHeader}>
                    <Text style={styles.strengthLabel}>
                      {i18n.t("passwordStrength")}:
                    </Text>
                    <Text
                      style={[
                        styles.strengthValue,
                        { color: passwordStrength.color },
                      ]}
                    >
                      {passwordStrength.label}
                    </Text>
                  </View>
                  <View style={styles.strengthBar}>
                    <View
                      style={[
                        styles.strengthProgress,
                        {
                          width: `${passwordStrength.score}%`,
                          backgroundColor: passwordStrength.color,
                        },
                      ]}
                    />
                  </View>
                </View>
              )}
            </View>

            {/* Confirm Password */}
            <View style={styles.fieldContainer}>
              <Text style={styles.fieldLabel}>
                {i18n.t("confirmPassword")}
              </Text>
              <View
                style={[
                  styles.passwordField,
                  confirmPassword.length > 0 &&
                    (confirmPassword === newPassword
                      ? styles.matchBorder
                      : styles.mismatchBorder),
                ]}
              >
                <TouchableOpacity
                  onPress={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={styles.eyeButton}
                >
                  <Text style={styles.eyeIcon}>
                    {showConfirmPassword ? "👁️" : "🙈"}
                  </Text>
                </TouchableOpacity>
                <TextInput
                  style={styles.passwordInput}
                  value={confirmPassword}
                  onChangeText={setConfirmPassword}
                  secureTextEntry={!showConfirmPassword}
                  placeholder="••••••••••••••••"
                  placeholderTextColor="#9CA3AF"
                />
                {confirmPassword.length > 0 && (
                  <Text style={styles.matchIcon}>
                    {confirmPassword === newPassword ? "✅" : "❌"}
                  </Text>
                )}
              </View>
              {confirmPassword.length > 0 &&
                confirmPassword !== newPassword && (
                  <Text style={styles.mismatchText}>
                    {i18n.t("passwordsNotMatch")}
                  </Text>
                )}
            </View>

            {/* Save Button */}
            <TouchableOpacity
              style={styles.saveButton}
              onPress={handleSave}
              disabled={loading}
            >
              {loading ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <Text style={styles.saveButtonText}>
                  {i18n.t("save")}
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>
    </div>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F5F5F5",
    minHeight: "100vh" as any,
  },
  containerRTL: {
    flexDirection: "row-reverse",
  } as any,
  mainContent: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
    padding: 20,
  },
  headerSection: {
    marginBottom: 30,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 16,
    paddingVertical: 16,
    paddingHorizontal: 24,
  },
  breadcrumb: {
    flexDirection: "row",
    alignItems: "center",
  },
  breadcrumbLink: {
    fontSize: 28,
    color: "#333",
  },
  breadcrumbSeparator: {
    fontSize: 28,
    color: "#999",
    marginHorizontal: 4,
  },
  breadcrumbCurrent: {
    fontSize: 32,
    fontWeight: "bold",
    color: "#333",
  },
  headerIconContainer: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: "#E8F4F8",
    borderWidth: 4,
    borderColor: "#1379A2",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  headerIcon: {
    width: 70,
    height: 70,
  },
  headerBar: {
    height: 8,
    backgroundColor: "#1379A2",
    borderRadius: 4,
    marginTop: 8,
  },
  messageContainer: {
    marginHorizontal: 40,
    marginBottom: 20,
    padding: 16,
    borderRadius: 12,
  },
  errorMessage: {
    backgroundColor: "#FEE2E2",
    borderColor: "#F87171",
    borderWidth: 1,
  },
  successMessage: {
    backgroundColor: "#D1FAE5",
    borderColor: "#34D399",
    borderWidth: 1,
  },
  messageText: {
    textAlign: "center",
    fontSize: 16,
    fontWeight: "500",
  },
  infoBox: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#DBEAFE",
    borderRadius: 12,
    padding: 16,
    marginHorizontal: 40,
    marginBottom: 24,
    gap: 12,
  },
  infoIcon: {
    fontSize: 22,
  },
  infoText: {
    flex: 1,
    fontSize: 16,
    color: "#1E40AF",
    textAlign: "right",
  },
  formContainer: {
    alignItems: "center",
    gap: 24,
    paddingHorizontal: 40,
  },
  fieldContainer: {
    width: "100%",
    maxWidth: 500,
  },
  fieldLabel: {
    fontSize: 16,
    color: "#666",
    textAlign: "right",
    marginBottom: 8,
  },
  passwordField: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    borderRadius: 30,
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderWidth: 1,
    borderColor: "#E0E0E0",
  },
  matchBorder: {
    borderColor: "#10B981",
    borderWidth: 2,
  },
  mismatchBorder: {
    borderColor: "#EF4444",
    borderWidth: 2,
  },
  eyeButton: {
    marginRight: 12,
  },
  eyeIcon: {
    fontSize: 22,
  },
  passwordInput: {
    flex: 1,
    fontSize: 18,
    color: "#333",
    textAlign: "right",
  },
  matchIcon: {
    fontSize: 18,
    marginLeft: 8,
  },
  mismatchText: {
    fontSize: 14,
    color: "#EF4444",
    textAlign: "right",
    marginTop: 4,
  },
  strengthContainer: {
    marginTop: 12,
    paddingHorizontal: 8,
  },
  strengthHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  strengthLabel: {
    fontSize: 14,
    color: "#666",
  },
  strengthValue: {
    fontSize: 14,
    fontWeight: "600",
  },
  strengthBar: {
    height: 8,
    backgroundColor: "#E5E7EB",
    borderRadius: 4,
    overflow: "hidden",
  },
  strengthProgress: {
    height: 8,
    borderRadius: 4,
  },
  saveButton: {
    width: "100%",
    maxWidth: 500,
    backgroundColor: "#1379A2",
    borderRadius: 30,
    paddingVertical: 16,
    alignItems: "center",
    marginTop: 16,
  },
  saveButtonText: {
    fontSize: 20,
    fontWeight: "600",
    color: "#FFFFFF",
  },
});

export default WebPasswordScreen;
